//common
import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useLoaderData } from "react-router-dom";
import { FunnelFill } from "react-bootstrap-icons";
import { List, RowComponentProps, ListImperativeAPI } from "react-window";

//satellite
import { VehicleSituation } from "./api/Network";
import { AbandonedVehicleLoaderData, Match } from "./loaders/abandonedVehicleLoader";
import { Button } from "./components/Button";
import { abandonedVehicleStatuses } from "./components/AbandonedVehicleStatusArray";
import { Api } from "./api/Api";
import { aerialImageTileSpec } from "./api/Url";
import { expandAbandonedVehicle } from "./loaders/abandonedVehicleCompressed";
import { CommandCard } from "./CommandCard";
import { showVehicleSituation } from "./api/EnumUtils";
import { AbandonedVehicleItemSituationMemo } from "./AbandonedVehicleItemSituation";
import DeckGL, { GeoJsonLayer, OrthographicView, OrthographicViewState, TileLayer } from "deck.gl";
import { createRoundedVehiclePolygon } from "./common/Geometry";
import { LanczosBitmapLayer } from "./components/LanczosBitmapLayer";
import { AnnotationFilter, AnnotationFilterModal } from "./components/AnnotationFilter";
import { Row } from "./AbandonedVehicleItem";

const initialValue: AnnotationFilter = {
    typeFilters:      [],
    situationFilters: [],
    statusFilters:    abandonedVehicleStatuses,
    confThr:          0.0,
    similarityThr:    0.0,
}

// row height for the virtualized list - 6em at the default 16px root
const ROW_HEIGHT = 124;

export function AbandonedVehicleSituationPage() {
    const [showFilters,   setShowFilters] = useState<boolean>( false );
    const [filters,       setFilters] = useState<AnnotationFilter>( initialValue );
    const { imageA, imageB, vehicles: initVehicles } = useLoaderData() as AbandonedVehicleLoaderData;
    const [vehicles,      setVehicles] = useState<Match[]>( initVehicles );
    const [ind,           setInd] = useState<number>();
    const [tool,          setTool] = useState<VehicleSituation>();
    const [urlIds, setUrlIdOverride] = useState<Set<number>>();
    const [viewState, setViewState] = useState<OrthographicViewState>( () => {
        const x = Math.round( imageB.width  / 2 );
        const y = Math.round( imageB.height / 2 );
        return {
            target: [x, y, 0],
            zoom:   1,
        }
    } );
    //refs
    const listRef = useRef<ListImperativeAPI>( null );
    function nextVehicle() { setInd( x => x !== undefined ? Math.min( x + 1, vehicles.length - 1 ) : undefined ); }
    function prevVehicle() { setInd( x => x !== undefined ? Math.max( x - 1, 0 )                   : undefined ); }

    const filteredIds = useMemo( () => {
        if( urlIds !== undefined ) {
            return urlIds;
        }
        const all = vehicles.toSorted( ( a, b ) => a.abandonedVehicleId - b.abandonedVehicleId );
        return new Set( all
                //.filter( v => v.displaySituation === null )
               .filter( v => filters.statusFilters.includes( v.status.status ) )
               .filter( v => !filters.confThr || ( v.confidence > filters.confThr ) )
               .filter( v => !filters.similarityThr || ( v.similarity > filters.similarityThr ) )
               .map( v => v.abandonedVehicleId )
        );
    }, [filters, urlIds] );

    const resultsFiltered = useMemo( () => {
        return vehicles
            .filter( v => filteredIds.has( v.abandonedVehicleId ) )
            .toSorted( ( a, b ) => a.abandonedVehicleId - b.abandonedVehicleId );
    }, [vehicles, filteredIds] );

    // resolve ?vehicleId=... from the URL to an index into resultsFiltered (runs once / on data change)
    useEffect( () => {
        const params = new URLSearchParams( window.location.search );
        const rawIds = params.get("ids");
        if( rawIds ) {
            const ids = rawIds.split( "," ).map( Number ).filter( n => !Number.isNaN( n ) );
            if( ids.length > 0 ) {
                setUrlIdOverride( new Set( ids ) );
            }
        }
        const raw = params.get( "vehicleId" );
        if( raw === null ) { 
            return;
        }
        const id = Number( raw );
        if( Number.isNaN( id ) ) { return; }
        const i = resultsFiltered.findIndex( v => v.abandonedVehicleId === id );
        if( i >= 0 ) { setInd( i ); }
    }, [] );

    // keep the URL in sync with the current selection so it can be shared / reloaded
    useEffect( () => {
        const url = new URL( window.location.href );
        if( ind !== undefined && resultsFiltered[ind] ) {
            url.searchParams.set( "vehicleId", String( resultsFiltered[ind].abandonedVehicleId ) );
        }
        else {
            url.searchParams.delete( "vehicleId" );
        }
        window.history.replaceState( null, "", url.toString() );
    }, [ind, resultsFiltered] );

    const handleMatchClick = useCallback( async ( match: Match ) => {
        setInd( resultsFiltered.findIndex( x => x.abandonedVehicleId === match.abandonedVehicleId ) );
    }, [resultsFiltered] );

    const selected = ind !== undefined ? resultsFiltered[ind] : undefined;

    useEffect( () => {
        if( !selected ) {
            return;
        }
        const { x, y, } = selected;
        setViewState( vs => ({ ...vs, target: [x, y], zoom: 2 }) );
    }, [ind] );

    function update( match: Match, situation: VehicleSituation ) {
        const result = Api.abandonedVehicleSituationUpdate( match.abandonedVehicleId, {
            situation: situation
        } );
        result.then( res => {
            if( !res.isOk ) {
                throw new Error( "Error saving vehicle" );
            }
            const vehicle = expandAbandonedVehicle( res.value );
            setTool( undefined );
            setVehicles( prev => prev.map( v => v.abandonedVehicleId === vehicle.abandonedVehicleId ? vehicle : v ) );
            nextVehicle();
        } );
        return;
    }

    useEffect( () => {
        const handleKeyDown = ( e: KeyboardEvent ) => {
            if( selected === undefined ) { return; }
            switch( e.key ) {
                case "z": setTool( VehicleSituation.Street     ); break;
                case "x": setTool( VehicleSituation.Shoulder   ); break;
                case "c": setTool( VehicleSituation.Sidewalk   ); break;
                case "v": setTool( VehicleSituation.Field      ); break;
                case "a": setTool( VehicleSituation.Alleyway   ); break;
                case "s": setTool( VehicleSituation.Driveway   ); break;
                case "d": setTool( VehicleSituation.ParkingLot ); break;
                case "q": setTool( VehicleSituation.Junkyard   ); break;
                case "w": setTool( VehicleSituation.Worksite   ); break;
                case "e": setTool( VehicleSituation.Industrial ); break;
                case "Enter": {
                    if( tool !== undefined ) { update( selected, tool ); }
                    break;
                }

                //prev and next
                case "ArrowDown": nextVehicle(); e.preventDefault(); break;
                case "ArrowUp":   prevVehicle(); e.preventDefault(); break;

                case "/": {
                    const v = selected;
                    window.open(
                        `https://remote.test:3000/detect/${imageA.aerialImageId}/${imageB.aerialImageId}/${v.abandonedVehicleId}`,
                        "_blank"
                    );
                    e.preventDefault();
                    break;
                }
            }
        };
        window.addEventListener( "keydown", handleKeyDown );
        return () => window.removeEventListener( "keydown", handleKeyDown );
    }, [selected, tool] );

    // when ind moves (e.g. via n/m keys), scroll the virtual list to keep it visible
    useEffect( () => {
        if( ind === undefined || !listRef.current ) { return; }
        const sel = resultsFiltered[ind];
        if( !sel ) { return; }
        const visibleIdx = resultsFiltered.findIndex( v => v.abandonedVehicleId === sel.abandonedVehicleId );
        if( visibleIdx >= 0 ) { listRef.current.scrollToRow( { index: visibleIdx, align: "smart" } ); }
    }, [resultsFiltered, ind] );

    const newPolygon = useMemo( () => {
        if( !selected ) {
            return [];
        }
        const poly = createRoundedVehiclePolygon( selected );
        return [...poly, poly[0]];
    }, [selected] );

    const bWidth    = Math.ceil( imageB.width  / 512.0 ) * 512;
    const bHeight   = Math.ceil( imageB.height / 512.0 ) * 512;
    const tileLayer = useMemo( () => new TileLayer<ImageBitmap>( {
            id:                 "aerial-tiles-b",
            pickable:           false,
            tileSize:           512,
            refinementStrategy: "best-available",
            minZoom:            -3,
            maxZoom:            0,
            extent:             [0, 0, bWidth, bHeight],
            data:               aerialImageTileSpec( imageB.aerialImageId ),
            renderSubLayers: props => {
                const [ [ left, bottom ], [ right, top ] ] = props.tile.boundingBox;
                const { data, ...otherProps } = props;
                return new LanczosBitmapLayer( otherProps, {
                    weight: 3,
                    image: data,
                    transitions: {
                        opacity: { duration: 150 },                        
                    },
                    bounds: [ left, top, right, bottom ],
                    textureParameters: {
                        minFilter: "nearest",
                        magFilter: "nearest"
                    }
                } );
            }
        } ), [ imageB, bWidth, bHeight ] );

    const previewLayer = useMemo( () => new GeoJsonLayer( {
        id: "create-preview",
        data: {
            type: "FeatureCollection" as const, features: newPolygon.length ? [{
                type: "Feature" as const,
                geometry: { type: "Polygon" as const, coordinates: [newPolygon] },
                properties: {},
            }] : []
        },
        getFillColor: [255, 255, 0, 40],
        getLineColor: [255, 255, 0, 200],
        getLineWidth: 0.5,
        lineWidthMinPixels: 2,
        coordinateSystem: "cartesian",
    } ), [newPolygon] );

    return <div className="h-100">
        <AnnotationFilterModal
            show={showFilters}
            filters={filters}
            setFilters={setFilters}
            onHide={() => setShowFilters( false )} />

        <div id="capture-list-page">
            <div id="capture-list">
                <div className="bg-body-tertiary border border-end-0 d-flex flex-row w-100 justify-content-center align-items-center gap-2 p-2">
                    <div className="flex-grow-1 ps-2 fs-5">
                        {ind} / {resultsFiltered.length} Detections 
                    </div>
                    <Button className="bg-body fs-6 px-2 py-1 rounded" onClick={() => setShowFilters( true )}>
                        <FunnelFill />
                    </Button>
                </div>
                <div style={{ minHeight: 0, height: "100%" }}>
                    <List
                        listRef={listRef}
                        rowComponent={Row}
                        rowCount={resultsFiltered.length}
                        rowHeight={ROW_HEIGHT}
                        rowProps={{ selectedId: selected?.abandonedVehicleId, vehicles: resultsFiltered, onClick: handleMatchClick }}
                        overscanCount={6} />
                </div>
            </div>
            <div className="image-viewer h-100 overflow-hidden position-relative" style={{ backgroundColor: "black" }}>
                <div style={{ fontSize: "13px" }}>
                    <CommandCard cmdSlots={[
                        { key: "q", active: false, color: "#ddd", label: "Junk" },
                        { key: "w", active: false, color: "#ddd", label: "Work" },
                        { key: "e", active: false, color: "#ddd", label: "Indust" },
                        null, null,
                        { key: "a", active: false, color: "#ddd", label: "Alley" },
                        { key: "s", active: false, color: "#ddd", label: "Drive" },
                        { key: "d", active: false, color: "#ddd", label: "Lot" },
                        null, null,
                        //bottom row
                        { key: "z", active: false, color: "#ddd", label: "Street" },
                        { key: "x", active: false, color: "#ddd", label: "Shoulder" },
                        { key: "c", active: false, color: "#ddd", label: "Sidewalk" },
                        { key: "v", active: false, color: "#ddd", label: "Field" },
                    ]} onSelect={() => { }} />
                </div>
                {tool && <div className="position-relative p-2 d-flex flex-column gap-2" style={{ left: "0.75ex", top: "0.75ex", zIndex: 5, width: "max-content" }}>
                    <div className="d-flex align-items-center justify-content-center rounded border border-black small p-2 bg-body text-body">
                        Press Enter to Mark as "{showVehicleSituation( tool )}"
                    </div>
                </div>}
                <DeckGL
                    views={new OrthographicView()}
                    layers={[
                        tileLayer,
                        previewLayer
                    ]}
                    initialViewState={ viewState }
                    controller
                    style={{ backgroundColor: "black" }}
                    getCursor={( { isDragging, isHovering } ) => {
                        if( isDragging ) { return "grabbing";  }
                        if( isHovering ) { return "pointer";   }
                        return "grab";
                    }}
                />
            </div>
        </div>
    </div>;
}